import { useForm, yupResolver } from "@mantine/form";
import TextInput from "src/components/TextInput/TextInput";
import Textarea from "src/components/Textarea/Textarea";
import MultiSelect from "src/components/MultiSelect";
import NumberInput from "src/components/NumberInput/NumberInput";
import { RoomFormProps, roomFormSchema } from "./CreateModal";
import {
  GenericConnectInput,
  InputMaybe,
  UpdateRoomInput,
  useDeleteRoomMutation,
  useGetLocationLazyQuery,
  useUpdateRoomMutation,
} from "src/generated/graphql";
import FormCard from "src/components/FormCard";
import { MESSAGES } from "src/utils/constants";
import { useDisclosure } from "@mantine/hooks";
import { useContext, useEffect } from "react";
import { AppContext } from "src/api/AppContext";

interface IUpdateRoomForm extends Omit<UpdateRoomInput, "services"> {
  services: string[] | InputMaybe<Array<GenericConnectInput>>;
}

const ActionRoomModalForm = ({
  refetchData,
  handleClose,
  roomData,
  isAllowWrite,
}: Required<RoomFormProps>) => {
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(true);
  const [updateRoom, { loading }] = useUpdateRoomMutation({
    onCompleted: () => {
      handleClose();
      refetchData();
    },
  });
  const [deleteRoom] = useDeleteRoomMutation({
    onCompleted: () => {
      refetchData();
    },
  });

  const { currentLocation } = useContext(AppContext);
  const [getLocation, { data: currentLocationInfo }] =
    useGetLocationLazyQuery();

  useEffect(() => {
    currentLocation && getLocation({ variables: { id: currentLocation?.id } });
  }, [currentLocation?.id]);

  const form = useForm<IUpdateRoomForm>({
    validate: yupResolver(roomFormSchema),
    initialValues: {
      name: roomData.name,
      description: roomData.description,
      totalCapacity: roomData.totalCapacity,
      services: roomData.services.map((val) => val.id),
    },
  });

  const handleUpdate = async ({ services, ...restValues }: IUpdateRoomForm) => {
    await updateRoom({
      variables: {
        data: {
          services: services!.map((service) => ({
            id: service as unknown as string,
          })),
          ...restValues,
        },
        updateRoomId: roomData.id,
      },
    });
  };

  const handleDelete = () => {
    deleteRoom({ variables: { deleteRoomId: roomData.id } });
  };

  const handleReset = () => {
    form.reset();
    toggleFormActive();
  };
  const isFormUpdated = form.isDirty() && form.isTouched();

  return (
    <FormCard
      title="Basic Info"
      loading={loading}
      bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
      onCancel={handleReset}
      onDelete={isAllowWrite ? handleDelete : undefined}
      onEdit={isAllowWrite ? toggleFormActive : undefined}
      onSave={form.onSubmit((values) => handleUpdate(values))}
      isFormActive={isFormActive}
    >
      <TextInput
        withAsterisk
        label="Name"
        {...form.getInputProps("name")}
        disabled={isFormActive}
      />
      <Textarea
        label="Description"
        {...form.getInputProps("description")}
        disabled={isFormActive}
      />
      <NumberInput
        withAsterisk
        label="Max Occupancy"
        type="number"
        {...form.getInputProps("totalCapacity")}
        disabled={isFormActive}
        min={1}
      />
      <MultiSelect
        data={
          (currentLocationInfo?.locationOne &&
            currentLocationInfo.locationOne.locationServices.map((val) => ({
              value: val.id,
              label: val.organizationService.name,
            }))) ||
          []
        }
        label="Supported Services"
        {...form.getInputProps("services")}
        disabled={isFormActive}
      />
    </FormCard>
  );
};

export default ActionRoomModalForm;
